"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { dropHidden, holdOrder, ORDER_RELEASE_MS, type HeldOrder, type RowList } from "@/lib/live-board/held-order";
import type { RowId } from "@/lib/odds-screen/live/types";

type ListSignals = {
  readonly isScrolling: (scrolling: boolean) => void;
  readonly atTopStateChange: (atTop: boolean) => void;
  readonly scrollerRef: (scroller: HTMLElement | Window | null) => void;
};

export type LapsedProps = { readonly "data-lapsed"?: ""; readonly inert?: true };

type RowMarks = {
  readonly lapsedProps: (id: RowId) => LapsedProps;
  readonly list: ListSignals;
};

export type HeldRows<R> = RowList<R> & RowMarks;

export type OrderHold<L extends RowList<unknown>> = HeldOrder<L> & RowMarks;

export type OrderCue = { readonly held: boolean; readonly release: () => void };

type Carried<L extends RowList<unknown>> = {
  readonly shown: HeldOrder<L>;
  readonly latest: L;
  readonly carry: boolean;
  readonly settings: readonly unknown[];
  readonly hides: readonly unknown[];
  readonly epoch: number;
};

const LIVE: LapsedProps = {};
const LAPSED_OPEN: LapsedProps = { "data-lapsed": "" };
const LAPSED: LapsedProps = { "data-lapsed": "", inert: true };

export function lapsedPropsOf(lapsed: { has(id: RowId): boolean }, openId: RowId | null): (id: RowId) => LapsedProps {
  return (id) => (!lapsed.has(id) ? LIVE : id === openId ? LAPSED_OPEN : LAPSED);
}

function sameValues(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((value, index) => Object.is(value, b[index]));
}

export function useOrderHold<F extends object, S extends object, H extends object, L extends RowList<unknown>>({ fold, feed, settings, hides, openId }: {
  readonly fold: (input: F & S & H) => L;
  readonly feed: F;
  readonly settings: S;
  readonly hides: H;
  readonly openId: RowId | null;
}): { readonly rows: OrderHold<L>; readonly live: L; readonly cue: OrderCue } {
  const settingValues = Object.values(settings);
  const hideValues = Object.values(hides);
  const latest = useMemo(() => fold({ ...feed, ...settings, ...hides }), [fold, ...Object.values(feed), ...settingValues, ...hideValues]);

  const [scrolling, setScrolling] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const scroller = useRef<HTMLElement | Window | null>(null);
  const list = useMemo<ListSignals>(() => ({
    isScrolling: setScrolling,
    atTopStateChange: setAtTop,
    scrollerRef: (element) => {
      scroller.current = element;
      if (element !== null) return;
      setScrolling(false);
      setAtTop(true);
    },
  }), []);

  const [epoch, setEpoch] = useState(0);
  const carried = useRef<Carried<L> | null>(null);
  const previous = carried.current;
  const keptIds = previous === null ? null : sameValues(previous.hides, hideValues) ? previous.shown.ids : dropHidden(previous.shown.ids, previous.latest.ids, latest.ids);
  const kept = previous === null || keptIds === null ? null : keptIds === previous.shown.ids ? previous.shown : { ...previous.shown, ids: keptIds };
  const fresh = previous === null || kept === null || previous.epoch !== epoch || !sameValues(previous.settings, settingValues);
  const board = fresh ? latest : kept;
  const wants = (openId !== null && board.ids.includes(openId)) || scrolling || !atTop;

  const [releasing, setReleasing] = useState(false);
  useEffect(() => {
    if (wants) {
      setReleasing(true);
      return;
    }
    const timer = setTimeout(() => setReleasing(false), ORDER_RELEASE_MS);
    return () => clearTimeout(timer);
  }, [wants]);
  const held = wants || releasing;

  const carry = held && !fresh;
  const shown = !fresh && previous.latest === latest && previous.carry === carry
    ? previous.shown
    : holdOrder(carry ? kept : null, latest);
  const next: Carried<L> = { shown, latest, carry, settings: settingValues, hides: hideValues, epoch };
  useLayoutEffect(() => {
    carried.current = next;
  });

  const rows = useMemo<OrderHold<L>>(() => ({
    ...shown,
    lapsedProps: lapsedPropsOf(shown.lapsed, openId),
    list,
  }), [shown, openId, list]);

  const release = useCallback(() => {
    setEpoch((value) => value + 1);
    scroller.current?.scrollTo({ top: 0 });
  }, []);
  const cue = useMemo<OrderCue>(() => ({ held, release }), [held, release]);

  return { rows, live: latest, cue };
}
